import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, app } from '../config/firebase.config';
import { User, SubscriptionStatus, SubscriptionType } from '../types/auth.types';
import { FirestoreService } from './firestore.service';

import { EmailService } from './email.service';

const functions = getFunctions(app);

export class AuthService {
  private static currentUser: User | null = null;
  private static listeners: ((user: User | null) => void)[] = [];

  static async signUp(email: string, password: string, userData: Partial<User>): Promise<User> {
    try {
      // Vérification pré-inscription via Cloud Function (Admin SDK, contourne les règles Firestore)
      const checkSignupEligibility = httpsCallable<
        { email: string },
        { canSignup: boolean; reason?: string; isFirstUser: boolean; invitation: { id: string; subscriptionType: string | null; subscriptionStatus: string | null } | null }
      >(functions, 'checkSignupEligibility');

      const result = await checkSignupEligibility({ email });
      const { canSignup, reason, isFirstUser, invitation } = result.data;

      if (!canSignup) {
        const error = new Error('Email already in use');
        (error as any).code = 'auth/email-already-in-use';
        throw error;
      }

      // Créer l'utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Préparer les données utilisateur
      const userDataToSave: User = {
        id: userCredential.user.uid,
        email: userCredential.user.email!,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        club: userData.club || '',
        phone: userData.phone || '',
        marketingSource: userData.marketingSource || '',
        marketingConsent: userData.marketingConsent || false,
        country: userData.country,
        language: userData.language,
        role: isFirstUser ? 'admin' : 'user',
        subscriptionStatus: (invitation?.subscriptionStatus as SubscriptionStatus) || (isFirstUser ? 'active' : 'inactive'),
        subscriptionType: (invitation?.subscriptionType as SubscriptionType) || (isFirstUser ? 'evolution' : 'basic'),
        subscriptionStartDate: invitation || isFirstUser ? Timestamp.fromDate(new Date()) : null,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Créer le profil Firestore + marquer l'invitation via Cloud Function (Admin SDK, bypass règles)
      const createUserProfileFn = httpsCallable<
        {
          userId: string;
          email: string;
          firstName: string;
          lastName: string;
          club: string;
          phone: string;
          marketingSource: string;
          marketingConsent: boolean;
          country?: string;
          language?: string;
          role: string;
          subscriptionStatus: string;
          subscriptionType: string;
          hasSubscription: boolean;
          invitationId?: string | null;
        },
        { success: boolean }
      >(functions, 'createUserProfile');

      await createUserProfileFn({
        userId: userCredential.user.uid,
        email: userCredential.user.email!,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        club: userData.club || '',
        phone: userData.phone || '',
        marketingSource: userData.marketingSource || '',
        marketingConsent: userData.marketingConsent || false,
        country: userData.country,
        language: userData.language,
        role: isFirstUser ? 'admin' : 'user',
        subscriptionStatus: (invitation?.subscriptionStatus as SubscriptionStatus) || (isFirstUser ? 'active' : 'inactive'),
        subscriptionType: (invitation?.subscriptionType as SubscriptionType) || (isFirstUser ? 'evolution' : 'basic'),
        hasSubscription: !!(invitation || isFirstUser),
        invitationId: invitation?.id || null,
      });

      // Envoyer l'email de bienvenue
      try {
        await EmailService.sendWelcomeEmail(email, userData.firstName || '', userData.language);
        console.log('✅ Email de bienvenue envoyé avec succès');
      } catch (error) {
        console.error('⚠️ Erreur lors de l\'envoi de l\'email de bienvenue:', error);
        // Ne pas bloquer l'inscription si l'email échoue
        // On pourrait implémenter une file d'attente pour réessayer plus tard
      }

      AuthService.currentUser = userDataToSave;
      AuthService.notifyListeners(userDataToSave);

      return userDataToSave;
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      throw error;
    }
  }

  static async signIn(email: string, password: string): Promise<User> {
    try {
      console.log('AuthService.signIn - Début de la connexion');
      
      // Étape 1: Authentification Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('AuthService.signIn - Authentification Firebase réussie', userCredential.user.uid);
      
      // Étape 2: Récupération des données Firestore avec retry
      let userData: User | null = null;
      let retries = 3;
      
      while (retries > 0 && !userData) {
        try {
          userData = await FirestoreService.getUserById(userCredential.user.uid);
          if (userData) {
            console.log('AuthService.signIn - Données utilisateur récupérées avec succès');
            break;
          }
        } catch (firestoreError) {
          console.warn(`AuthService.signIn - Tentative ${4 - retries}/3 échouée pour récupérer les données Firestore`, firestoreError);
          retries--;
          if (retries > 0) {
            // Attendre 500ms avant de réessayer
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      if (!userData) {
        console.error('AuthService.signIn - Impossible de récupérer les données utilisateur après 3 tentatives');
        throw new Error('Impossible de récupérer les données utilisateur. Veuillez réessayer.');
      }

      AuthService.currentUser = userData;
      AuthService.notifyListeners(userData);
      
      console.log('AuthService.signIn - Connexion complète avec succès');
      return userData;
    } catch (error) {
      console.error('AuthService.signIn - Erreur lors de la connexion:', error);
      throw error;
    }
  }

  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
      AuthService.currentUser = null;
      AuthService.notifyListeners(null);
      // Vider le cache Firestore
      FirestoreService.clearCache();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      throw error;
    }
  }

  static async resetPassword(email: string): Promise<void> {
    try {
      console.log('AuthService.resetPassword - Envoi de l\'email de réinitialisation à', email);
      await sendPasswordResetEmail(auth, email);
      console.log('AuthService.resetPassword - Email envoyé avec succès');
    } catch (error) {
      console.error('AuthService.resetPassword - Erreur lors de l\'envoi de l\'email:', error);
      throw error;
    }
  }

  private static notifyListeners(user: User | null) {
    this.listeners.forEach(listener => listener(user));
  }

  static addListener(listener: (user: User | null) => void) {
    this.listeners.push(listener);
  }

  static removeListener(listener: (user: User | null) => void) {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  static async getCurrentUser(): Promise<User | null> {
    if (AuthService.currentUser) {
      return AuthService.currentUser;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) return null;

    try {
      const userData = await FirestoreService.getUserById(user.uid);
      if (userData) {
        AuthService.currentUser = userData;
        return userData;
      }
      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'utilisateur:", error);
      return null;
    }
  }
}
